import type { ZohoContact } from '@deltasuite/zoho-client';
import { masterMark } from '@deltasuite/ui-tokens';
import { useState } from 'react';
import { MOCK_CONTACTS } from '../api/mock';
import { QueuedForMeBanner } from '../campaigns/QueuedForMeBanner';
import type { SavedFilter } from '../api/saved-filters';
import { ContactList } from './ContactList';
import { EMPTY_FILTER, FilterPanel, toContactFilter, type FilterPanelValue } from './FilterPanel';
import { useFilterMatches } from './useFilterMatches';
import { useSavedFiltersHook } from './useSavedFiltersHook';

const POOL_SIZE = MOCK_CONTACTS.length;
const SPRINT_BATCH = 30;

interface Props {
  onStart: (contacts: readonly ZohoContact[]) => void;
}

function panelFromFilter(row: SavedFilter): FilterPanelValue {
  const f = row.filter;
  return {
    states: [...(f.state ?? [])],
    projectTypes: [],
    advancedTypes: [...(f.companyType ?? [])],
    hasMobile: f.hasMobile === true,
    lastActivityAfter: f.lastActivityAfter ? f.lastActivityAfter.slice(0, 10) : null,
    lastActivityBefore: f.lastActivityBefore ? f.lastActivityBefore.slice(0, 10) : null,
  };
}

export function SprintModeScreen({ onStart }: Props) {
  const [filter, setFilter] = useState<FilterPanelValue>(EMPTY_FILTER);
  const [saveName, setSaveName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const saved = useSavedFiltersHook();

  const matches = useFilterMatches(MOCK_CONTACTS, toContactFilter(filter));

  const onSave = async () => {
    const name = saveName.trim();
    if (!name) return;
    await saved.create({ name, filter: toContactFilter(filter) });
    setSaveName('');
  };

  const loadFilter = async (row: SavedFilter) => {
    setFilter(panelFromFilter(row));
    await saved.update(row.id, { touch_last_used: true }).catch(() => undefined);
  };

  const startRename = (row: SavedFilter) => {
    setRenamingId(row.id);
    setRenameDraft(row.name);
  };

  const commitRename = async () => {
    if (!renamingId) return;
    const name = renameDraft.trim();
    if (name) await saved.update(renamingId, { name }).catch(() => undefined);
    setRenamingId(null);
    setRenameDraft('');
  };

  return (
    <main className="min-h-full px-8 py-10">
      <header className="flex items-center gap-3 text-graphite-700">
        <span className="font-sans text-2xl leading-none text-signal-amber" aria-hidden>
          {masterMark}
        </span>
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-graphite-500">
          DeltaDial
        </span>
      </header>

      <div className="mt-6">
        <QueuedForMeBanner />
      </div>

      <section className="mt-8 grid gap-12 lg:grid-cols-[20rem_1fr]">
        <div>
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
            Build a call list
          </h1>
          <p className="mt-2 font-sans text-sm leading-relaxed text-graphite-700">
            Filter the pool, preview the count, then start a sprint.
          </p>

          <div className="mt-8">
            <FilterPanel value={filter} onChange={setFilter} />
          </div>

          <div className="mt-8 rounded-lg border border-graphite-100 bg-surface-sunk p-4">
            <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
              Save as
            </h3>
            <div className="mt-2 flex gap-2">
              <input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="Architects NSW + ACT"
                className="flex-1 rounded-md border border-graphite-300 bg-surface px-3 py-2 font-sans text-sm text-graphite-900"
              />
              <button
                type="button"
                onClick={() => void onSave()}
                disabled={!saveName.trim()}
                className="rounded-md bg-graphite-900 px-3 py-2 font-sans text-xs font-medium text-surface transition-colors hover:bg-graphite-700 disabled:opacity-40"
              >
                Save
              </button>
            </div>

            {saved.loading && saved.rows.length === 0 ? (
              <p className="mt-3 font-mono text-[0.7rem] text-graphite-500">loading…</p>
            ) : saved.rows.length > 0 ? (
              <ul className="mt-4 space-y-1.5">
                {saved.rows.map((row) => (
                  <li key={row.id} className="font-mono text-xs">
                    <div className="flex items-center justify-between gap-2">
                      {renamingId === row.id ? (
                        <input
                          autoFocus
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onBlur={() => void commitRename()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void commitRename();
                            if (e.key === 'Escape') {
                              setRenamingId(null);
                              setRenameDraft('');
                            }
                          }}
                          className="flex-1 rounded border border-graphite-300 bg-surface px-2 py-1 text-graphite-900"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => void loadFilter(row)}
                          className="flex-1 truncate text-left text-graphite-700 hover:text-graphite-900"
                        >
                          {row.name}
                          {row.remember_last ? (
                            <span className="ml-1.5 text-[0.6rem] uppercase text-signal-amber">
                              ★
                            </span>
                          ) : null}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => startRename(row)}
                        className="text-graphite-500 hover:text-graphite-900"
                        aria-label={`Rename ${row.name}`}
                        title="Rename"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        onClick={() => void saved.remove(row.id)}
                        className="text-graphite-500 hover:text-signal-amber"
                        aria-label={`Delete ${row.name}`}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>
                    <label className="ml-1 mt-1 flex items-center gap-1.5 text-[0.65rem] text-graphite-500">
                      <input
                        type="checkbox"
                        checked={row.remember_last}
                        onChange={(e) =>
                          void saved.update(row.id, { remember_last: e.target.checked })
                        }
                        className="size-3 accent-signal-amber"
                      />
                      remember as default
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 font-mono text-[0.7rem] text-graphite-500">
                {saved.error
                  ? saved.error
                  : 'No saved filters yet. Saving uses Supabase when the worker is wired; otherwise stays in this tab.'}
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <h2 className="font-sans text-base font-medium text-graphite-700">Matches</h2>
            <p className="font-mono text-xs text-graphite-500">
              <span className="text-graphite-900">{matches.length}</span> of {POOL_SIZE} contacts ·{' '}
              <span className="text-signal-amber">
                start with first {Math.min(matches.length, 30)}
              </span>
            </p>
          </div>
          <div className="mt-4">
            <ContactList contacts={matches} total={matches.length} />
          </div>
          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="font-mono text-[0.7rem] text-graphite-500">
              Preview build — mock contacts. Live Zoho data arrives in production.
            </p>
            <button
              type="button"
              onClick={() => onStart(matches.slice(0, SPRINT_BATCH))}
              disabled={matches.length === 0}
              className="rounded-md bg-signal-amber px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-signal-amber-hover disabled:opacity-40"
            >
              Start sprint with first {Math.min(matches.length, SPRINT_BATCH)}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
